import React from 'react';
import SelectionCard from '../SelectionCard';
import toast from 'react-hot-toast';

/**
 * AncestrySection - Select ancestry and community
 */
function AncestrySection({ character, onUpdate, allData }) {
  const ancestries = allData.ancestries || {};
  const communities = allData.communities || {};

  function handleAncestrySelect(ancestryId) {
    const currentAncestry = character.ancestry || [];

    let newAncestry;
    if (currentAncestry.includes(ancestryId)) {
      // Deselect - remove from array
      newAncestry = currentAncestry.filter(id => id !== ancestryId);
    } else {
      // Select - add to array (max 2 for mixed ancestry)
      if (currentAncestry.length >= 2) {
        // Already have 2 ancestries selected (mixed ancestry limit)
        toast.error('You can only select up to 2 ancestries for mixed ancestry');
        return;
      }
      newAncestry = [...currentAncestry, ancestryId];
    }

    // Set features based on selection
    let ancestryFeatures = [];
    if (newAncestry.length === 1) {
      // Single ancestry: use all features
      ancestryFeatures = ancestries[newAncestry[0]]?.features || [];
    } else if (newAncestry.length === 2) {
      // Mixed ancestry: features will be selected via radio buttons (clear them for now)
      ancestryFeatures = [];
    }

    onUpdate({
      ancestry: newAncestry,
      ancestryFeatures
    });
  }

  function handleMixedAncestryFeatureSelect(ancestryId, feature) {
    const otherAncestryId = character.ancestry.find(id => id !== ancestryId);

    // Find existing feature from the OTHER ancestry (not the current one)
    const existingOtherFeature = character.ancestryFeatures.find(f => {
      // Check if this feature belongs to the other ancestry
      return ancestries[otherAncestryId]?.features?.some(otherFeat =>
        otherFeat.name === f.name && otherFeat.text === f.text
      );
    });

    // Build new features array: selected feature + existing feature from other ancestry
    let newFeatures = [{...feature}];
    if (existingOtherFeature) {
      newFeatures.push(existingOtherFeature);
    }

    onUpdate({ ancestryFeatures: newFeatures });
  }

  function handleCommunitySelect(communityId) {
    onUpdate({
      community: communityId
    });
  }

  return (
    <div className="space-y-6">
      {/* Ancestry selection */}
      <div>
        <h4 className="text-heading-sm text-red-400 font-serif mb-3">Choose Your Ancestry</h4>
        <p className="text-gray-300 text-small mb-4">
          Your ancestry determines your heritage and innate abilities.
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          {Object.entries(ancestries).map(([id, ancestryData]) => (
            <SelectionCard
              key={id}
              title={ancestryData.name}
              description={ancestryData.description}
              features={ancestryData.features}
              isSelected={character.ancestry?.includes(id)}
              onClick={() => handleAncestrySelect(id)}
            />
          ))}
        </div>

        {/* Mixed Ancestry Feature Selection */}
        {character.ancestry && character.ancestry.length === 2 && (
          <div className="mt-6 p-4 bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-lg">
            <h4 className="text-heading-sm text-red-400 font-serif mb-2">Select Features for Mixed Ancestry</h4>
            <p className="text-gray-300 text-small mb-4">
              Choose one feature from each ancestry.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {character.ancestry.map((ancestryId) => {
                const ancestry = ancestries[ancestryId];
                return (
                  <div key={ancestryId} className="space-y-3">
                    <h5 className="text-sm font-medium text-white">
                      {ancestry.name}
                    </h5>
                    {ancestry.features?.map((feat, i) => {
                      const isSelected = character.ancestryFeatures?.some(
                        f => f.name === feat.name && f.text === feat.text
                      );
                      return (
                        <SelectionCard
                          key={i}
                          title={feat.name}
                          description={feat.text}
                          isSelected={isSelected}
                          onClick={() => handleMixedAncestryFeatureSelect(ancestryId, feat)}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Community selection */}
      <div>
        <h4 className="text-heading-sm text-red-400 font-serif mb-3">Choose Your Community</h4>
        <p className="text-gray-300 text-small mb-4">
          Your community represents where you come from and your cultural background.
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          {Object.entries(communities).map(([id, communityData]) => (
            <SelectionCard
              key={id}
              title={communityData.name}
              description={communityData.description}
              features={communityData.features}
              isSelected={character.community === id}
              onClick={() => handleCommunitySelect(id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default AncestrySection;
